import { useEffect, useRef, useState } from 'preact/hooks';
import { renderIf } from '../../utils/render.utils';
import LoadingScreen from '../../components/shared/LoadingScreen/LoadingScreen';
import CardService from '../../services/card.service';
import Stepper from '../../components/shared/Stepper/Stepper';
import Step from '../../components/shared/Stepper/Step';
import ActivationCodeStep from '../../components/CardActivation/ActivationCodeStep';
import CardNumberStep from '../../components/CardActivation/CardNumberStep';
import AppBar from '../../components/shared/AppBar/AppBar';
import CardActivationStatusPage from '../CardActivationStatusPage/CardActivationStatusPage';
import useAndroidBackPress from '../../hooks/back.listener.hook';
import NavigationUtils from '../../utils/navigation.utils';
import useTranslation from '../../localization/i18n.context';
import { TrackingEvent } from '../../sdk/tracking.event';
import OnboardingService from '../../services/onboarding.service';
import {
  UserApplicationStatus,
  UserIdentityStatus,
} from '../../utils/onboarding-status-url.utils';
import CardStatus from '../../constants/card.status';

export default function CardActivationPage({ organization: organizationCode }) {
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(1);
  const { getLanguageId } = useTranslation();

  const [data, setData] = useState({
    activationCode: '',
    cardNumber: '',
  });
  const backRef = useRef(null);

  const handleInput = (key, value) => {
    const temp = { ...data, [key]: value };
    setData(temp);
  };

  const handleStepChange = e => {
    e.preventDefault();
    setActiveStep(s => s + 1);
  };

  const handleFormSubmit = e => {
    e.preventDefault();
    setLoading(true);
    CardService.activate({
      cardUid: data.activationCode,
      lastDigits: data.cardNumber,
      organizationCode,
      languageId: getLanguageId(),
    })
      .then(response => {
        NavigationUtils.navigate(NavigationUtils.Routes.SET_PIN, true, {
          cardUid: data.activationCode,
          cardNumber: data.cardNumber,
        });
      })
      .catch(err => {
        if (
          err.statusCode == 400 &&
          err.body.status === CardActivationStatusPage.Type.MAX_ATTEMPTS
        ) {
          NavigationUtils.navigate(
            `${NavigationUtils.Routes.CARD_ACTIVATION_INFO}/${CardActivationStatusPage.Type.MAX_ATTEMPTS}`,
          );
        } else if (err.statusCode == 400) {
          NavigationUtils.navigate(
            `${NavigationUtils.Routes.CARD_ACTIVATION_INFO}/${CardActivationStatusPage.Type.TRY_AGAIN}`,
          );
        } else {
          NavigationUtils.navigate(
            `${NavigationUtils.Routes.CARD_ACTIVATION_STATUS}/error`,
          );
        }
      });
  };

  useAndroidBackPress(backRef.current, () => {
    backRef.current.props.onClick();
  });

  useEffect(() => {
    OnboardingService.getStatus(organizationCode)
      .then(({ data }) => {
        const userApplication = (data.userApplications || []).find(
          userApplication =>
            userApplication.organizationCode === organizationCode,
        );

        if (
          !data?.needsOcr &&
          !data?.needsOnboarding &&
          userApplication?.status === UserApplicationStatus.APPROVED &&
          data?.userIdentity?.status === UserIdentityStatus.VALIDATED
        ) {
          return CardService.get(organizationCode).then(response => {
            const card = response.data?.[0];

            if (
              card == null ||
              card.uid == null ||
              card.status === CardStatus.ISSUED
            ) {
              setLoading(false);
              return;
            }

            return NavigationUtils.navigate(
              NavigationUtils.Routes.WALLET,
              true,
              {
                organization: organizationCode,
              },
            );
          });
        }

        return NavigationUtils.navigate(NavigationUtils.Routes.KYC, true, {
          organization: organizationCode,
        });
      })

      .catch(err => {
        console.error(`Something went wrong`, err);
        NavigationUtils.navigate(NavigationUtils.Routes.TRY_AGAIN);
      });
  }, []);

  return (
    <>
      {renderIf(loading, <LoadingScreen />)}
      <AppBar
        onBackClick={() => {
          if (activeStep > 1) {
            setActiveStep(s => s - 1);
          } else {
            TrackingEvent.trigger(
              TrackingEvent.Events.ACTIVATION_LEAVE_TAP,
              TrackingEvent.Sources.CAAS,
            );
            NavigationUtils.back();
          }
        }}
        backRef={backRef}
        className="fixed-bar"
      />

      <form onSubmit={handleFormSubmit}>
        <Stepper activeStep={activeStep}>
          <Step>
            <ActivationCodeStep
              value={data.activationCode}
              onInput={handleInput}
              onButtonClick={handleStepChange}
              organizationCode={organizationCode}
            />
          </Step>
          <Step>
            <CardNumberStep
              value={data.cardNumber}
              onInput={handleInput}
              organizationCode={organizationCode}
            />
          </Step>
        </Stepper>
      </form>
    </>
  );
}
